<template>
  <div class="card py-5 px-5">
    <b-steps has-navigation>
      <b-step-item step="1" label="Select">
        <b-field label="Type">
          <b-select placeholder="Change user type" v-model="selectUserType">
            <option value="all">All</option>
            <option value="participants">
              Participants ({{ this.participants.length }})
            </option>
            <option value="feedbackgivers" :disabled="!feedbackGivers.length">
              Feedback Givers ({{ this.feedbackGivers.length }})
            </option>
          </b-select>
        </b-field>
        <b-table
          :data="filteredData"
          :checked-rows.sync="form.recipients"
          checkable
          paginated
          pagination-simple
          per-page="8"
        >
          <b-table-column field="name" label="Name" v-slot="props">
            {{ props.row.name }}
          </b-table-column>

          <b-table-column field="email" label="Email" v-slot="props">
            {{ props.row.email }}
          </b-table-column>

          <b-table-column
            field="last_login"
            label="Last Login"
            centered
            v-slot="props"
          >
            <b-tooltip
              position="is-left"
              :label="props.row.last_login"
              :active="props.row.last_login"
              type="is-success"
            >
              <span
                class="tag"
                :class="[props.row.last_login ? 'is-success' : 'is-danger']"
              >
                {{
                  props.row.last_login
                    ? dateFromNow(props.row.last_login)
                    : 'Never'
                }}
              </span>
            </b-tooltip>
          </b-table-column>
        </b-table>
      </b-step-item>
      <b-step-item step="2" label="Compose" clickable>
        <b-field label="Email Subject">
          <b-input v-model="form.emailTitle"></b-input>
        </b-field>
        <p><b>Email Content</b></p>
        <TextEditor :content.sync="form.emailContent" minHeight="200" />
        <b-collapse :open="false">
          <template #trigger="{ open }">
            <div
              class="has-background-info px-2 py-2"
              style="border-radius: 2px"
            >
              <strong class="has-text-white"
                >{{ open ? 'Hide' : 'Show' }} Variables</strong
              >
            </div>
          </template>
          <div class="notification">
            <div class="">
              <ul>
                <!-- prettier-ignore -->
                <li v-pre>{{firstName}} - <b>User's first name</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{lastName}} - <b>User's last name</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{email}} - <b>User's email</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{clientName}} - <b>Client Name</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{projectName}} - <b>Name of the project</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{startDate}} - <b>Project Start Date</b></li>
                <li v-pre>
                  <!-- prettier-ignore -->
                  {{initialEndDate}} - <b>Project Initial End Date</b>
                </li>
                <!-- prettier-ignore -->
                <li v-pre>{{endDate}} - <b>Project Final End Date</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{scoring}} - <b>List of scores</b></li>
                <li v-pre>
                  <!-- prettier-ignore -->
                  {{scoringTable}} - <b>List of scores as a table</b>
                </li>
                <!-- prettier-ignore -->
                <li v-pre>{{linkedParticipants}} - <b>Comma seperated list of participants associated with a feedback giver</b></li>
                <!-- prettier-ignore -->
                <li v-pre>{{firstParticipant}} - <b>Returns the name of the first participant associated with a feedback giver</b></li>
              </ul>
            </div>
          </div>
        </b-collapse>
      </b-step-item>
      <b-step-item step="3" label="Preview" clickable>
        <h3>Recipients ({{ form.recipients.length }})</h3>
        <b-taglist class="mt-2" v-if="form.recipients.length">
          <b-tag
            type="is-info"
            v-for="user in form.recipients.slice(0, 9)"
            :key="user.id"
            >{{ user.email }}</b-tag
          >
          <b-tag v-if="form.recipients.length > 9"
            >+{{ form.recipients.length - 9 }} other{{
              form.recipients.length - 9 > 1 ? 's' : ''
            }}</b-tag
          >
        </b-taglist>
        <p v-else>You need to select at least one user</p>
        <hr />
        <b-collapse :open="false">
          <template #trigger="{ open }">
            <div
              class="is-flex is-flex-direction-row is-align-items-center"
              style="gap: 0.25rem"
            >
              <h3>Preview Email Content</h3>
              <b-icon
                pack="fas"
                :icon="[open ? 'chevron-down' : 'chevron-up']"
                size="is-small"
              ></b-icon>
            </div>
          </template>
          <div class="notification">
            <strong v-html="form.emailTitle"></strong>
            <p v-html="form.emailContent"></p>
          </div>
        </b-collapse>
      </b-step-item>
      <template #navigation="{ previous, next }">
        <div class="is-flex is-justify-content-space-between">
          <b-button
            outlined
            type="is-danger"
            icon-pack="fas"
            icon-left="backward"
            :disabled="previous.disabled"
            @click.prevent="previous.action"
          >
            Previous
          </b-button>
          <b-button
            v-if="!next.disabled"
            outlined
            type="is-success"
            icon-pack="fas"
            icon-right="forward"
            :disabled="next.disabled"
            @click.prevent="next.action"
          >
            Next
          </b-button>
          <b-button
            v-else
            outlined
            type="is-success"
            icon-pack="fas"
            icon-right="paper-plane"
            @click="submit"
            :disabled="!canSend"
            >Confirm &amp; Send
          </b-button>
        </div>
      </template>
    </b-steps>
  </div>
</template>

<script>
import TextEditor from '@/Components/Management/TextEditor';

export default {
  components: {
    TextEditor,
  },
  props: {
    users: Array,
    project: String,
  },
  data() {
    return {
      selectUserType: 'all',
      form: {
        recipients: [],
        emailContent: '',
        emailTitle: '',
      },
    };
  },
  computed: {
    filteredData() {
      if (this.selectUserType === 'participants') {
        return this.participants;
      }
      if (this.selectUserType === 'feedbackgivers') {
        return this.feedbackGivers;
      }
      return this.users;
    },
    participants() {
      return this.users.filter((u) => u.isParticipant);
    },
    feedbackGivers() {
      return this.users.filter((u) => u.isFeedbackGiver);
    },
    canSend() {
      return (
        this.form.recipients.length > 1 &&
        this.form.emailContent.replace(/(<([^>]+)>)/gi, '') &&
        this.form.emailTitle.replace(/(<([^>]+)>)/gi, '')
      );
    },
  },
  methods: {
    selectParticipants() {
      this.recipients = this.participants;
    },
    removeParticipant(user) {
      this.recipients = this.recipients.filter((u) => u.id !== user.id);
    },
    submit() {
      this.$inertia.post(
        route('projects.custom-email', this.project),
        this.form,
        {
          preserveScroll: true,
          onSuccess: () => {
            this.$emit('close');
            this.$buefy.toast.open({
              message: 'Email successfully sent',
              type: 'is-success',
              position: 'is-top',
            });
          },
        }
      );
    },
  },
};
</script>

<style>
.ql-align-right {
  text-align: right;
}
.ql-align-center {
  text-align: center;
}
.ql-align-left {
  text-align: left;
}
</style>
